/** Minha conta › Agenda no Outlook/Google/iPhone: link privado de calendário (só leitura). */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { confirmar } from "./feedback";
import { Card, CopyButton, ErrorBox, Loading, useAct } from "./kit";

interface Opts { fatal: boolean; audiencias: boolean; planejadas: boolean; interno: boolean }
interface Info { active: boolean; createdAt?: string; lastFetchAt?: string | null; options: Opts }
const OPT_LABEL: Record<keyof Opts, string> = { fatal: "Prazos fatais", audiencias: "Audiências", planejadas: "Atividades planejadas", interno: "Prazos internos" };

export function CalendarFeedCard(): JSX.Element {
  const key = ["conta-calendario"];
  const { data, error } = useQuery({ queryKey: key, queryFn: () => api.get<Info>("/conta/calendario") });
  const [url, setUrl] = useState<string | null>(null);
  const create = useAct((o: Opts) => api.post<{ url: string }>("/conta/calendario", o), [key], (r) => setUrl(r.url), "Link criado");
  const save = useAct((o: Opts) => api.put("/conta/calendario", o), [key]);
  const revoke = useAct(() => api.del("/conta/calendario"), [key], () => setUrl(null), "Link desativado");
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const o = data.options;
  return (
    <Card title="Minha agenda no Outlook, Google ou iPhone">
      <p className="muted small" style={{ marginTop: 0 }}>
        Gera um link privado de calendário com as suas atividades (como responsável ou participante). Assine no Outlook
        ("Adicionar calendário › Da internet"), no Google Agenda ("Outras agendas › Do URL") ou no iPhone (Ajustes › Calendário › Contas).
        É só leitura e atualiza sozinho (a cada uma ou poucas horas, conforme o aplicativo). Não compartilhe o link.
      </p>
      <div className="row wrap gap-8">
        {(Object.keys(OPT_LABEL) as Array<keyof Opts>).map((k) => (
          <label key={k} className="check"><input type="checkbox" checked={o[k]} onChange={(e) => (data.active ? save : { mutate: () => undefined }).mutate({ ...o, [k]: e.target.checked })} disabled={!data.active} /> {OPT_LABEL[k]}</label>
        ))}
      </div>
      {url && <div className="banner-ok" style={{ marginTop: 10 }}>
        Copie agora (ele não aparece de novo): <span className="mono small" style={{ wordBreak: "break-all" }}>{url}</span> <CopyButton text={url} title="Copiar link" />
      </div>}
      <div className="row gap-8" style={{ marginTop: 10 }}>
        {!data.active ? <button className="btn primary small" disabled={create.isPending} onClick={() => create.mutate(o)}>Gerar link do calendário</button> : <>
          <span className="muted small">Ativo desde {fmtDateTime(data.createdAt!)}{data.lastFetchAt ? ` · última leitura ${fmtDateTime(data.lastFetchAt)}` : " · ainda não foi lido"}</span>
          <button className="btn small" onClick={async () => { if (await confirmar("Gerar um link novo? O atual deixa de funcionar.")) create.mutate(o); }}>Gerar novo link</button>
          <button className="btn small ghost" onClick={async () => { if (await confirmar("Desativar o link? A agenda some do Outlook/Google na próxima atualização.")) revoke.mutate(); }}>Desativar</button>
        </>}
      </div>
      <ErrorBox error={create.error ?? save.error ?? revoke.error} />
    </Card>
  );
}
